// ─────────────────────────────────────────────
//  useTasks — live view of today's task directives for one facility.
//
//  ONE hook for BOTH surfaces (the board's send/ledger card and the phone's
//  dashboard) so the two can never disagree about what was sent or who has
//  seen it. Sorting and status live in domain/tasks.js; this is only the
//  subscription.
//
//  ⚠️ SCOPED BY FACILITY *AND* DAY, both required.
//    · facility alone leaks another campus's directives onto this board;
//    · day alone resurrects yesterday's "go home" at tomorrow's 7 AM huddle.
//  Passing a falsy facilityId or dateKey subscribes to NOTHING and returns an
//  empty list rather than falling back to a broader query — a directive list
//  that quietly widens its own scope is worse than an empty one.
// ─────────────────────────────────────────────
import { useEffect, useMemo, useState } from "react";
import { listenToTasks } from "../firebase/firestore";
import { onAuth } from "../firebase/auth";
import { sortTasksNewestFirst } from "../domain/tasks";

export function useTasks(facilityId, dateKey) {
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Subscribe only while signed in, and RE-subscribe on every sign-in — the
  // useStaff precedent. A listener started pre-auth is denied, dies, and leaves
  // the list permanently empty until a manual refresh, which on this feature
  // would read as "the board sent you nothing".
  useEffect(() => {
    let unsubTasks = null;
    const unsubAuth = onAuth((authUser) => {
      if (unsubTasks) { unsubTasks(); unsubTasks = null; }
      if (!authUser || !facilityId || !dateKey) {
        setRaw([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      unsubTasks = listenToTasks(
        facilityId,
        dateKey,
        (snap) => {
          setRaw(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error("Task directives listener failed:", err);
          setError(err);
          setLoading(false);
        }
      );
    });
    return () => {
      if (unsubTasks) unsubTasks();
      unsubAuth();
    };
  }, [facilityId, dateKey]);

  const tasks = useMemo(() => sortTasksNewestFirst(raw), [raw]);
  return { tasks, loading, error };
}

export default useTasks;
